/**
 * Word Wagon — the board's model, with no Phaser in it (specs/wagon-mode.md §5).
 *
 * Everything that decides what a landing *means* lives here: what the wagon catches, what piles up
 * behind it, when a wagon is wrecked and what shipping a full one clears. The scene draws the
 * consequences; it never works them out. Same split as `game/maze/grid.ts` and `game/pairs/deal.ts`,
 * and for the same reason — this is the half that has to be testable in node.
 */
import { WAGON } from '@/game/config';

/** One word in the freighter's hold: what it says, and whether the sign asks for it. */
export interface WagonWord {
  id: string;
  text: string;
  /** True when the word belongs to the class on the wagon's sign — cargo rather than hazard. */
  target: boolean;
}

/** The board between two ships: the wagon's load, the pile behind it and the yard behind that. */
export interface WagonState {
  lanes: number;
  /** Crates a lane may hold before it overflows (§5.4). */
  stackCap: number;
  /** Words caught into the wagon so far. */
  load: number;
  /** Words this wagon holds when full (§4.1). */
  capacity: number;
  /**
   * Spare wagons in the yard — this run's lives, drawn as 🚃. A wrecked wagon is swapped for one of
   * these and its cargo shovelled across, which is why the load survives a hazard (§5.2): the thing
   * that breaks is the wagon, never the reading already done.
   */
  wagons: number;
  /** The pile per lane, bottom crate first — missed targets, still readable on their crates. */
  crates: string[][];
  /**
   * Nothing has gone wrong since this wagon rolled in: no wagon wrecked AND no crate landed. What
   * the perfect-wagon refund is paid on (§5.5) — it rewards reading *everything*, which is why a
   * missed target spoils it even though it costs nothing by itself.
   */
  perfect: boolean;
}

/** What a word's landing did. */
export type Landing =
  /** A target landed in the wagon. */
  | { kind: 'caught' }
  /** A hazard landed in the wagon: it wrecks one, and a spare rolls up. The load is NOT touched. */
  | { kind: 'burst' }
  /** A target reached the pile: one more crate in its lane. */
  | { kind: 'crate' }
  /** A target reached a lane that was already full: it buries a wagon, and the lane is swept clear. */
  | { kind: 'overflow' }
  /** A hazard reached the rail with nobody under it — through the grate, gone, free. */
  | { kind: 'through' };

/** The value at `round`'s rung of a ladder whose last rung repeats forever (§4.1). */
function rung<T>(ladder: readonly T[], round: number): T {
  const i = Math.min(Math.max(1, Math.floor(round)), ladder.length) - 1;
  return ladder[i];
}

export const lanesFor = (round: number): number => rung(WAGON.lanes, round);
export const loadFor = (round: number): number => rung(WAGON.load, round);
export const fallMsFor = (round: number): number => rung(WAGON.fallMs, round);
export const spawnMsFor = (round: number): number => rung(WAGON.spawnMs, round);
export const targetRatioFor = (round: number): number => rung(WAGON.targetRatio, round);
export const stackCapFor = (round: number): number => rung(WAGON.stackCap, round);

/**
 * The reading budget the ladder is actually tightening (§4.2): the fall, minus the longest drive
 * the wagon could have to make. Not used by the game — the scene has no need for it — but it is the
 * number the ladder is tuned against, so it is defined once, here, where the test can read it.
 */
export function decisionWindowMs(round: number): number {
  return fallMsFor(round) - WAGON.laneMs * (lanesFor(round) - 1);
}

/** An empty wagon at the given rung. */
export function newWagon(round: number, wagons: number): WagonState {
  const lanes = lanesFor(round);
  return {
    lanes,
    stackCap: stackCapFor(round),
    load: 0,
    capacity: loadFor(round),
    wagons,
    crates: Array.from({ length: lanes }, () => []),
    perfect: true,
  };
}

/**
 * How far up a lane its pile reaches, as a fraction of the fall (0 = empty lane, 1 = full).
 *
 * **Only targets are measured against this.** A missed target lands on the pile, so a clogged lane
 * hands its words over sooner and the child has less time to read them — the pressure §4.2
 * describes, and it is charged against reading time rather than against a life. A hazard falls the
 * whole way whatever is in the lane (it goes through the grate, §5.2), which is not just flavour:
 * it is what keeps the generator's avoid-every-hazard guarantee exact, since no pile a player
 * builds can move a hazard's landing earlier than the schedule said (§7.1).
 */
export function pileFraction(state: WagonState, lane: number, crateFraction: number): number {
  const n = state.crates[lane]?.length ?? 0;
  return Math.min(1, n * crateFraction);
}

/**
 * One word arriving, with the wagon standing in `wagonLane`. Pure: returns the state that follows
 * rather than editing the one it was given, so a test can assert on both.
 *
 * The four outcomes are the whole rule set of §5.2–§5.4, and the two that cost a wagon are the two
 * kinds of misreading: a hazard caught ("I thought that was a verb") and a lane overflowed ("I kept
 * not noticing the verbs").
 */
export function resolveLanding(
  state: WagonState,
  word: WagonWord,
  lane: number,
  wagonLane: number,
): { state: WagonState; landing: Landing } {
  const caught = lane === wagonLane;

  if (word.target && caught) {
    return { state: { ...state, load: state.load + 1 }, landing: { kind: 'caught' } };
  }

  if (!word.target && caught) {
    // The load survives on purpose: words already read and caught are earned, and taking them back
    // would punish one mistake twice — worst of all on the last word of a nearly full wagon.
    return {
      state: { ...state, wagons: Math.max(0, state.wagons - 1), perfect: false },
      landing: { kind: 'burst' },
    };
  }

  if (!word.target) return { state, landing: { kind: 'through' } };

  // A missed target: onto the pile, unless the pile is full.
  const crates = state.crates.map((c, i) => (i === lane ? [...c] : c));
  if (crates[lane].length >= state.stackCap) {
    crates[lane] = [];
    return {
      state: {
        ...state, crates, wagons: Math.max(0, state.wagons - 1), perfect: false,
      },
      landing: { kind: 'overflow' },
    };
  }
  crates[lane].push(word.text);
  return { state: { ...state, crates, perfect: false }, landing: { kind: 'crate' } };
}

/** The wagon is full and rolls off (§5.5). */
export function wagonFull(state: WagonState): boolean {
  return state.load >= state.capacity;
}

/**
 * Ship it: the crates go with it, the next rung's empty wagon rolls in, and a perfect load pays a
 * wagon back to the yard — capped at the starting five, so the refund can top a run up but never
 * inflate it.
 *
 * The sweep is the only thing that clears the board, which is the point: a child who reads well is
 * handed a clean board every few seconds, and one who does not watches the runway shorten.
 */
export function shipWagon(state: WagonState, nextRound: number): WagonState {
  const refunded = state.perfect
    ? Math.min(WAGON.startWagons, state.wagons + 1)
    : state.wagons;
  return newWagon(nextRound, refunded);
}

/** Travel time between two lanes — the wagon's speed is constant across the whole ladder (§4.1). */
/**
 * The lane a pointer is over, as a FRACTION — the exact inverse of the scene's `laneX`
 * (`laneW * (lane + 0.5)`), left unrounded on purpose.
 *
 * Steering used to round this down, which is why a drag moved the wagon in hops: between two lane
 * boundaries the target never changed, and then it changed by a whole lane at once and restarted
 * the travel tween. A drag was a rapid series of taps. Fractional targets make it a pursuit
 * instead — travelMs scales with distance, so a small movement of the finger resolves almost
 * immediately and the wagon reads as following the hand.
 *
 * It still only ever sets a DESTINATION. The wagon drives there under its own speed and is never
 * placed at the pointer, which is the rule the whole mode rests on (specs/wagon-mode.md §5.1):
 * putting the wagon under the finger would remove the decision the mode exists to ask for.
 */
export function laneAtPointer(x: number, laneW: number, lanes: number): number {
  if (laneW <= 0) return 0;
  return Math.min(Math.max(x / laneW - 0.5, 0), lanes - 1);
}

export function travelMs(from: number, to: number): number {
  return Math.abs(from - to) * WAGON.laneMs;
}

/**
 * Where the wagon is at `t`, driving from `from` toward `to` since `startedAt`. Lane units, so the
 * scene can position it between lanes; a landing is judged on the rounded lane it is nearest.
 *
 * There is no teleport: a tap sets a destination, it does not place the wagon (§5.1). Without that
 * the decision window of §4.2 evaporates and the mode is Survival with extra steps.
 */
export function wagonAt(from: number, to: number, startedAt: number, t: number): number {
  const total = travelMs(from, to);
  if (total <= 0) return to;
  const k = Math.min(1, Math.max(0, (t - startedAt) / total));
  return from + (to - from) * k;
}
